<script lang="ts" setup>
import { MediaImageFragment, ParagraphImage } from '~/api/graphql/generated'

const props = defineProps<{
  entity: ParagraphImage
}>()

const image = computed(() => props.entity.fieldImage?.entity as MediaImageFragment)
const description = computed(() => props.entity.fieldDescription?.processed)
const format = computed(() => props.entity.fieldFormat)

type ImageType = 'image-type-small' | 'image-type-medium' | 'image-type-large' | 'image-type-default'

const layout = computed((): { image: { class: string; type: ImageType } } => {
  if (format.value === 's') {
    return {
      image: {
        class: 'image image-type-small t-10 t-o-1 tl-7 tl-o-0 ds-o-1',
        type: 'image-type-small',
      },
    }
  }

  if (format.value === 'm') {
    return {
      image: {
        class: 'image image-type-medium',
        type: 'image-type-medium',
      },
    }
  }

  if (format.value === 'l') {
    return {
      image: {
        class: 'image image-type-large',
        type: 'image-type-large',
      },
    }
  }

  return {
    image: {
      class: 'image image-type-default t-11 tl-7 ds-8',
      type: 'image-type-default',
    },
  }
})
</script>

<template>
  <Section>
    <div v-animate="{ animation: 'vertical-reveal' }" :class="layout.image.class">
      <Image v-if="image" :entity="image" :type="layout.image.type" />
      <div v-if="description" class="caption text-xsmall" v-html="description" />
    </div>
  </Section>
</template>

<style lang="scss" scoped>
.image {
  &.image-type-default {
    --ml: 0;
    margin-left: var(--ml);

    @include breakpoint(tl) {
      --sides-width: max(
        0px,
        calc(min(100vw, var(--layout-max-width)) - var(--container-max-width) - 2 * var(--container-padding))
      );
      --ml: calc(((var(--sides-width) / 2) + var(--layout-padding)) * -1);
    }

    .caption {
      margin-left: calc(var(--ml) * -1);
    }
  }

  &.image-type-small {
  }

  &.image-type-medium {
    :deep(img) {
      aspect-ratio: 5/ 3;
    }
  }

  &.image-type-large {
    --sides-width: max(
      0px,
      calc(min(100vw, var(--layout-max-width)) - var(--container-max-width) - 2 * var(--container-padding))
    );
    --mi: calc(((var(--sides-width) / 2) + var(--layout-padding)) * -1);

    margin-inline: var(--mi);
    .caption {
      margin-left: calc(var(--mi) * -1);
    }
    :deep(img) {
      aspect-ratio: 5/ 3;
    }
  }
}

.caption {
  color: var(--c-text-light);
  max-width: 580px;
  @include fluid(margin-top, 18px, 24px);
  :deep(p) {
    margin-bottom: 0;
  }
}

.image {
  :deep(img) {
    display: block;
    width: 100%;
  }
}
</style>
